import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import api from '../services/api';
import {
  createAdminTemplate,
  deleteAdminTemplate,
  fetchAdminTemplates,
  updateAdminTemplate,
  fetchEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
} from '../services/admin';

const CATEGORY_OPTIONS = ['abbreviation', 'medication', 'anatomy', 'procedure', 'other'];

const emptyEntry = {
  term: '',
  expansion: '',
  phonetic: '',
  category: 'abbreviation',
  auto_expand: true,
};

const emptyMeetingTemplateForm = {
  key: '',
  name: '',
  category: '',
  description: '',
  tags: '',
  prompt_template: '',
  is_active: true,
};

const emptyEmailTemplateForm = {
  key: '',
  name: '',
  description: '',
  subject_template: '',
  body_template: '',
  is_active: true,
};

const AdminPage = () => {
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState({ ...emptyEntry });
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [aiTemplate, setAiTemplate] = useState('');
  const [aiTemplateUpdatedAt, setAiTemplateUpdatedAt] = useState(null);
  const [isTemplateDirty, setIsTemplateDirty] = useState(false);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);
  const [vocabExpanded, setVocabExpanded] = useState(false);
  const [meetingTemplates, setMeetingTemplates] = useState([]);
  const [isMeetingTemplateModalOpen, setIsMeetingTemplateModalOpen] = useState(false);
  const [meetingTemplateForm, setMeetingTemplateForm] = useState({ ...emptyMeetingTemplateForm });
  const [editingMeetingTemplateKey, setEditingMeetingTemplateKey] = useState(null);
  const [isMeetingTemplateSaving, setIsMeetingTemplateSaving] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isEmailTemplateModalOpen, setIsEmailTemplateModalOpen] = useState(false);
  const [emailTemplateForm, setEmailTemplateForm] = useState({ ...emptyEmailTemplateForm });
  const [editingEmailTemplateKey, setEditingEmailTemplateKey] = useState(null);
  const [isEmailTemplateSaving, setIsEmailTemplateSaving] = useState(false);

  const loadEntries = async () => {
    try {
      const { data } = await api.get('/api/stt/dictionary/organization');
      setEntries(data);
    } catch (error) {
      console.error('Failed to load organisation dictionary:', error);
      toast.error('Unable to load organisation vocabulary');
    }
  };

  const loadAiTemplate = async () => {
    try {
      const { data } = await api.get('/api/stt/settings/ai-prompt-template');
      setAiTemplate(data.template || '');
      setAiTemplateUpdatedAt(data.updated_at || null);
      setIsTemplateDirty(false);
    } catch (error) {
      console.error('Failed to load AI prompt template:', error);
      toast.error(error.response?.data?.detail || 'Unable to load AI prompt template');
    }
  };

  const handleTemplateSave = async () => {
    const trimmed = aiTemplate.trim();
    if (!trimmed) {
      toast.error('Template cannot be empty');
      return;
    }

    setIsTemplateSaving(true);
    try {
      await api.put('/api/stt/settings/ai-prompt-template', { template: trimmed });
      toast.success('AI prompt template updated');
      await loadAiTemplate();
    } catch (error) {
      console.error('Failed to update AI prompt template:', error);
      toast.error(error.response?.data?.detail || 'Failed to update AI prompt template');
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const handleTemplateReset = async () => {
    if (!window.confirm('Restore the default AI prompt template?')) {
      return;
    }
    setIsTemplateSaving(true);
    try {
      await api.delete('/api/stt/settings/ai-prompt-template');
      toast.success('AI prompt template reset to default');
      await loadAiTemplate();
    } catch (error) {
      console.error('Failed to reset AI prompt template:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset AI prompt template');
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const loadMeetingTemplates = async () => {
    try {
      const templates = await fetchAdminTemplates();
      setMeetingTemplates(templates);
    } catch (error) {
      console.error('Failed to load meeting templates:', error);
      toast.error('Unable to load meeting templates');
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const templates = await fetchEmailTemplates();
      setEmailTemplates(templates);
    } catch (error) {
      console.error('Failed to load email templates:', error);
      toast.error('Unable to load email templates');
    }
  };

  const openMeetingTemplateModal = (template) => {
    if (template) {
      setEditingMeetingTemplateKey(template.key);
      setMeetingTemplateForm({
        key: template.key,
        name: template.name,
        category: template.category || '',
        description: template.description || '',
        tags: Array.isArray(template.tags) ? template.tags.join(', ') : '',
        prompt_template: template.prompt_template || '',
        is_active: !!template.is_active,
      });
    } else {
      setEditingMeetingTemplateKey(null);
      setMeetingTemplateForm({ ...emptyMeetingTemplateForm });
    }
    setIsMeetingTemplateModalOpen(true);
  };

  const closeMeetingTemplateModal = () => {
    setIsMeetingTemplateModalOpen(false);
    setEditingMeetingTemplateKey(null);
    setMeetingTemplateForm({ ...emptyMeetingTemplateForm });
    setIsMeetingTemplateSaving(false);
  };

  const handleMeetingTemplateSave = async () => {
    const trimmedKey = meetingTemplateForm.key.trim();
    if (!trimmedKey) {
      toast.error('Template key is required');
      return;
    }
    if (!meetingTemplateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!meetingTemplateForm.prompt_template.trim()) {
      toast.error('Prompt template is required');
      return;
    }
    if (!meetingTemplateForm.prompt_template.includes('{{diarized_transcript}}')) {
      toast.error('Prompt template must include {{diarized_transcript}}');
      return;
    }

    const payload = {
      key: trimmedKey,
      name: meetingTemplateForm.name.trim(),
      category: meetingTemplateForm.category.trim() || null,
      description: meetingTemplateForm.description.trim() || null,
      tags: meetingTemplateForm.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      prompt_template: meetingTemplateForm.prompt_template.trim(),
      is_active: meetingTemplateForm.is_active,
    };

    setIsMeetingTemplateSaving(true);
    try {
      if (editingMeetingTemplateKey) {
        await updateAdminTemplate(editingMeetingTemplateKey, payload);
        toast.success('Meeting template updated');
      } else {
        await createAdminTemplate(payload);
        toast.success('Meeting template created');
      }
      await loadMeetingTemplates();
      closeMeetingTemplateModal();
    } catch (error) {
      console.error('Failed to save meeting template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save meeting template');
    } finally {
      setIsMeetingTemplateSaving(false);
    }
  };

  const handleMeetingTemplateDelete = async (template) => {
    const templateLabel = template.name || template.key;
    if (!window.confirm(`Delete "${templateLabel}" meeting template?`)) {
      return;
    }
    try {
      const response = await deleteAdminTemplate(template.key);
      const message =
        response?.status === 'deactivated'
          ? 'Template deactivated because it is in use'
          : 'Template deleted';
      toast.success(message);
      await loadMeetingTemplates();
    } catch (error) {
      console.error('Failed to delete meeting template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete meeting template');
    }
  };

  const openEmailTemplateModal = (template) => {
    if (template) {
      setEditingEmailTemplateKey(template.key);
      setEmailTemplateForm({
        key: template.key,
        name: template.name,
        description: template.description || '',
        subject_template: template.subject_template || '',
        body_template: template.body_template || '',
        is_active: !!template.is_active,
      });
    } else {
      setEditingEmailTemplateKey(null);
      setEmailTemplateForm({ ...emptyEmailTemplateForm });
    }
    setIsEmailTemplateModalOpen(true);
  };

  const closeEmailTemplateModal = () => {
    setIsEmailTemplateModalOpen(false);
    setEditingEmailTemplateKey(null);
    setEmailTemplateForm({ ...emptyEmailTemplateForm });
    setIsEmailTemplateSaving(false);
  };

  const handleEmailTemplateSave = async () => {
    const trimmedKey = emailTemplateForm.key.trim();
    if (!trimmedKey) {
      toast.error('Template key is required');
      return;
    }
    if (!emailTemplateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!emailTemplateForm.subject_template.trim()) {
      toast.error('Subject template is required');
      return;
    }
    if (!emailTemplateForm.body_template.trim()) {
      toast.error('Body template is required');
      return;
    }

    const payload = {
      key: trimmedKey,
      name: emailTemplateForm.name.trim(),
      description: emailTemplateForm.description.trim() || null,
      subject_template: emailTemplateForm.subject_template.trim(),
      body_template: emailTemplateForm.body_template.trim(),
      is_active: emailTemplateForm.is_active,
    };

    setIsEmailTemplateSaving(true);
    try {
      if (editingEmailTemplateKey) {
        await updateEmailTemplate(editingEmailTemplateKey, payload);
        toast.success('Email template updated');
      } else {
        await createEmailTemplate(payload);
        toast.success('Email template created');
      }
      await loadEmailTemplates();
      closeEmailTemplateModal();
    } catch (error) {
      console.error('Failed to save email template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save email template');
    } finally {
      setIsEmailTemplateSaving(false);
    }
  };

  const handleEmailTemplateDelete = async (template) => {
    const templateLabel = template.name || template.key;
    if (!window.confirm(`Delete "${templateLabel}" email template?`)) {
      return;
    }
    try {
      await deleteEmailTemplate(template.key);
      toast.success('Email template deleted');
      await loadEmailTemplates();
    } catch (error) {
      console.error('Failed to delete email template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete email template');
    }
  };

  useEffect(() => {
    loadEntries();
    loadAiTemplate();
    loadMeetingTemplates();
    loadEmailTemplates();
  }, []);

  const filteredEntries = useMemo(() => {
    return entries
      .filter((entry) => {
        if (categoryFilter !== 'all' && entry.category !== categoryFilter) {
          return false;
        }
        if (!searchTerm) return true;
        return (
          entry.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.expansion.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [entries, categoryFilter, searchTerm]);

  const sortedMeetingTemplates = useMemo(() => {
    return [...meetingTemplates].sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [meetingTemplates]);

  const sortedEmailTemplates = useMemo(() => {
    return [...emailTemplates].sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [emailTemplates]);

  const openModal = (entry) => {
    if (entry) {
      setEditingId(entry.id);
      setFormState({
        term: entry.term,
        expansion: entry.expansion,
        phonetic: entry.phonetic || '',
        category: entry.category || 'abbreviation',
        auto_expand: entry.auto_expand ?? true,
      });
    } else {
      setEditingId(null);
      setFormState({ ...emptyEntry });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormState({ ...emptyEntry });
    setIsSaving(false);
  };

  const handleSave = async () => {
    if (!formState.term.trim() || !formState.expansion.trim()) {
      toast.error('Term and expansion are required');
      return;
    }

    const payload = {
      term: formState.term.trim(),
      expansion: formState.expansion.trim(),
      phonetic: formState.phonetic.trim() || null,
      category: formState.category,
      auto_expand: !!formState.auto_expand,
    };

    try {
      setIsSaving(true);
      if (editingId) {
        await api.put(`/api/stt/dictionary/organization/${editingId}`, payload);
        toast.success('Entry updated');
      } else {
        await api.post('/api/stt/dictionary/organization', payload);
        toast.success('Entry added');
      }
      await loadEntries();
      closeModal();
    } catch (error) {
      console.error('Failed to save organisation entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save entry');
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this organisation entry?')) {
      return;
    }
    try {
      await api.delete(`/api/stt/dictionary/organization/${id}`);
      toast.success('Entry deleted');
      await loadEntries();
    } catch (error) {
      console.error('Failed to delete organisation entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <span role="img" aria-label="cog">🛠️</span>
          Admin Console
        </h1>
        <p className="text-gray-600">
          Configure organisation-wide behaviour for AI corrections and shared vocabulary.
        </p>
        <div className="mt-3 flex gap-4 text-sm">
          <Link to="/admin/settings" className="text-blue-600 hover:underline">Meeting LLM settings</Link>
          <Link to="/admin/ai-usage" className="text-blue-600 hover:underline">AI usage</Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">AI Correction Prompt Template</h2>
            <p className="text-gray-600 mb-2">
              Customise the template used when sending transcription text to Azure OpenAI. Use placeholders
              like <code className="font-mono bg-gray-100 px-1 rounded">{'{{TRANSCRIPTION}}'}</code> and
              <code className="font-mono bg-gray-100 px-1 rounded ml-1">{'{{INSTRUCTIONS}}'}</code> to
              reference the captured text and user guidance.
            </p>
            <p className="text-sm text-gray-500">
              Available placeholders: <code className="font-mono bg-gray-100 px-1 rounded">{'{{TRANSCRIPTION}}'}</code>,{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{INSTRUCTIONS}}'}</code>,{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{RAW_INSTRUCTIONS}}'}</code>,{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{DEFAULT_INSTRUCTIONS}}'}</code>.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {aiTemplateUpdatedAt
                ? `Last updated ${new Date(aiTemplateUpdatedAt).toLocaleString()}`
                : 'Using default template'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleTemplateReset}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              disabled={isTemplateSaving}
            >
              Reset to default
            </button>
            <button
              type="button"
              onClick={handleTemplateSave}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm disabled:opacity-50"
              disabled={!isTemplateDirty || isTemplateSaving}
            >
              {isTemplateSaving ? 'Saving...' : 'Save template'}
            </button>
          </div>
        </div>
        <textarea
          value={aiTemplate}
          onChange={(e) => {
            setAiTemplate(e.target.value);
            setIsTemplateDirty(true);
          }}
          placeholder="Enter the AI correction prompt template..."
          className="w-full min-h-[220px] px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          spellCheck={false}
          disabled={isTemplateSaving}
        />
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Email templates</h2>
            <p className="text-sm text-gray-600 max-w-3xl">
              Customise notification emails such as meeting sharing invitations. Templates support{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{placeholders}}'}</code> for details like{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{meeting_title}}'}</code> and{' '}
              <code className="font-mono bg-gray-100 px-1 rounded">{'{{meeting_link}}'}</code>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openEmailTemplateModal(null)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm"
          >
            + Add template
          </button>
        </div>
        {sortedEmailTemplates.length === 0 ? (
          <div className="px-6 py-10 text-sm text-gray-500">
            No email templates configured yet. Add one to override the default notification wording.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedEmailTemplates.map((template) => (
                  <tr key={template.key} className="hover:bg-gray-50">
                    <td className="px-6 py-4 space-y-1">
                      <div className="text-sm font-semibold text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-500">Key: {template.key}</div>
                      {template.description && (
                        <p className="text-xs text-gray-500">{template.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="font-mono text-xs text-gray-900 break-words">
                        {template.subject_template}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 break-words max-h-12 overflow-hidden">
                        {template.body_template}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium space-x-3">
                      <button
                        onClick={() => openEmailTemplateModal(template)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleEmailTemplateDelete(template)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Meeting templates</h2>
            <p className="text-sm text-gray-600 max-w-3xl">
              Manage meeting prompts used for diarization summaries. Prompt templates must include the
              <code className="font-mono bg-gray-100 px-1 rounded ml-1">{'{{diarized_transcript}}'}</code> placeholder so the LLM can access speaker data.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openMeetingTemplateModal(null)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm"
          >
            + Add template
          </button>
        </div>
        {sortedMeetingTemplates.length === 0 ? (
          <div className="px-6 py-10 text-sm text-gray-500">
            No meeting templates configured yet. Add a template to surface a ready-made summary workflow.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedMeetingTemplates.map((template) => {
                  const tags = Array.isArray(template.tags) ? template.tags : [];
                  return (
                    <tr key={template.key} className="hover:bg-gray-50">
                      <td className="px-6 py-4 space-y-1">
                        <div className="text-sm font-semibold text-gray-900">{template.name}</div>
                        <div className="text-xs text-gray-500">Key: {template.key}</div>
                        {template.description && (
                          <p className="text-xs text-gray-500">{template.description}</p>
                        )}
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {tags.map((tag) => (
                              <span
                                key={`${template.key}-${tag}`}
                                className="px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{template.category || '—'}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            template.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {template.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium space-x-3">
                        <button
                          onClick={() => openMeetingTemplateModal(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleMeetingTemplateDelete(template)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Organisation Vocabulary</h2>
            <p className="text-sm text-gray-500">
              Collapsed by default to keep the admin console manageable.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {entries.length} term{entries.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => setVocabExpanded((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              aria-expanded={vocabExpanded}
            >
              {vocabExpanded ? 'Collapse panel' : 'Expand vocabulary'}
              <span
                className={`h-3 w-3 border-t border-r border-current transform transition-transform ${
                  vocabExpanded ? '-rotate-45' : 'rotate-135'
                }`}
              />
            </button>
          </div>
        </div>
        {!vocabExpanded ? (
          <div className="px-5 py-6 text-sm text-gray-500 border-t border-gray-200">
            Click "Expand vocabulary" to manage organisation entries, search, and categories.
          </div>
        ) : (
          <>
            <div className="px-5 pb-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search term or expansion..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All categories</option>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <button
                    onClick={() => openModal(null)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm"
                  >
                    + Add Entry
                  </button>
                </div>
              </div>
              <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800 mb-5">
                Tip: organisation entries automatically supplement every user's personal vocabulary.
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Term
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expansion
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auto-expand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phonetic
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {entry.term}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {entry.expansion}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 text-xs font-semibold uppercase tracking-wide">
                          {entry.category || 'uncategorised'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            entry.auto_expand
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {entry.auto_expand ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.phonetic || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                        <button
                          onClick={() => openModal(entry)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredEntries.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                        No entries found. Try adjusting your filters or add the first organisation term.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Term
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expansion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auto-expand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phonetic
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {entry.term}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {entry.expansion}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 text-xs font-semibold uppercase tracking-wide">
                      {entry.category || 'uncategorised'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        entry.auto_expand
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {entry.auto_expand ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.phonetic || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                    <button
                      onClick={() => openModal(entry)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {filteredEntries.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    No entries found. Try adjusting your filters or add the first organisation term.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Organisation Entry' : 'Add Organisation Entry'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Term *
                </label>
                <input
                  type="text"
                  value={formState.term}
                  onChange={(e) => setFormState((prev) => ({ ...prev, term: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., BP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expansion *
                </label>
                <input
                  type="text"
                  value={formState.expansion}
                  onChange={(e) => setFormState((prev) => ({ ...prev, expansion: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Blood pressure"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formState.category}
                    onChange={(e) => setFormState((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phonetic (optional)
                  </label>
                  <input
                    type="text"
                    value={formState.phonetic}
                    onChange={(e) => setFormState((prev) => ({ ...prev, phonetic: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., bee-pee"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-md">
                <div>
                  <p className="text-sm font-medium text-gray-900">Auto-expand abbreviation</p>
                  <p className="text-xs text-gray-500">
                    When enabled, this term expands automatically for everyone in your organisation.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormState((prev) => ({ ...prev, auto_expand: !prev.auto_expand }))
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formState.auto_expand ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formState.auto_expand ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : editingId ? 'Update Entry' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEmailTemplateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingEmailTemplateKey ? 'Edit email template' : 'Add email template'}
              </h3>
              <p className="text-sm text-gray-500">
                Use Mustache-style placeholders (e.g. <code className="font-mono bg-gray-100 px-1 rounded">{'{{meeting_title}}'}</code>) to inject meeting details.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template key *
                  </label>
                  <input
                    type="text"
                    value={emailTemplateForm.key}
                    onChange={(e) =>
                      setEmailTemplateForm((prev) => ({ ...prev, key: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., meeting_share_notification"
                    disabled={!!editingEmailTemplateKey}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={emailTemplateForm.name}
                    onChange={(e) =>
                      setEmailTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Display name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={emailTemplateForm.description}
                  onChange={(e) =>
                    setEmailTemplateForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Optional description for admin context"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject template *
                </label>
                <input
                  type="text"
                  value={emailTemplateForm.subject_template}
                  onChange={(e) =>
                    setEmailTemplateForm((prev) => ({ ...prev, subject_template: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder='e.g., Access granted: "{{meeting_title}}"'
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body template *
                </label>
                <textarea
                  value={emailTemplateForm.body_template}
                  onChange={(e) =>
                    setEmailTemplateForm((prev) => ({ ...prev, body_template: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={8}
                  placeholder="Hello {{recipient_name_or_email}}, ..."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="email-template-active"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  checked={emailTemplateForm.is_active}
                  onChange={(event) =>
                    setEmailTemplateForm((prev) => ({ ...prev, is_active: event.target.checked }))
                  }
                />
                <label htmlFor="email-template-active" className="text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeEmailTemplateModal}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                disabled={isEmailTemplateSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleEmailTemplateSave}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                type="button"
                disabled={isEmailTemplateSaving}
              >
                {isEmailTemplateSaving
                  ? 'Saving…'
                  : editingEmailTemplateKey
                  ? 'Update template'
                  : 'Create template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isMeetingTemplateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingMeetingTemplateKey ? 'Edit meeting template' : 'Add meeting template'}
              </h2>
              <button
                onClick={closeMeetingTemplateModal}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template key *
                  </label>
                  <input
                    type="text"
                    value={meetingTemplateForm.key}
                    onChange={(e) =>
                      setMeetingTemplateForm((prev) => ({ ...prev, key: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="meeting.sales"
                    disabled={!!editingMeetingTemplateKey}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template name *
                  </label>
                  <input
                    type="text"
                    value={meetingTemplateForm.name}
                    onChange={(e) =>
                      setMeetingTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Sales catch-up summary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={meetingTemplateForm.category}
                    onChange={(e) =>
                      setMeetingTemplateForm((prev) => ({ ...prev, category: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional category"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={meetingTemplateForm.description}
                  onChange={(e) =>
                    setMeetingTemplateForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Optional description for this template"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={meetingTemplateForm.tags}
                  onChange={(e) =>
                    setMeetingTemplateForm((prev) => ({ ...prev, tags: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., sales, product"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt template *
                </label>
                <textarea
                  value={meetingTemplateForm.prompt_template}
                  onChange={(e) =>
                    setMeetingTemplateForm((prev) => ({ ...prev, prompt_template: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={8}
                  spellCheck={false}
                  placeholder="Enter the LLM prompt template..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include <code className="font-mono bg-gray-100 px-1 rounded">{'{{diarized_transcript}}'}</code> and any other placeholders you need.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="meeting-template-active"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  checked={meetingTemplateForm.is_active}
                  onChange={(event) =>
                    setMeetingTemplateForm((prev) => ({ ...prev, is_active: event.target.checked }))
                  }
                />
                <label htmlFor="meeting-template-active" className="text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeMeetingTemplateModal}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                disabled={isMeetingTemplateSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleMeetingTemplateSave}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                type="button"
                disabled={isMeetingTemplateSaving}
              >
                {isMeetingTemplateSaving
                  ? 'Saving…'
                  : editingMeetingTemplateKey
                  ? 'Update template'
                  : 'Create template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
